import { useCallback } from 'react';
import { log } from 'log';
import { prop } from 'ramda';
import { useGetPlayerTags } from 'server';
import * as urlUtils from 'js/utils/url.util';
import { useNavigate } from 'js/hooks/navigate';
import { useTranslations } from 'js/hooks/i18n';
import { storageService } from 'js/services/storage.service';
import { useAppSelector, useDeviceSelector } from 'js/store/hooks';

const KYC_1_TAG_NAME = 'risk/kyc_x1';
const KYC_2_TAG_NAME = 'risk/kyc_x2';
const VERIFIED_TAG_NAME = 'uam/verification/conversion_verified';
const UPLOAD_REQUIREMENT_TAG_NAME = 'risk/doc/upload_requirement';
const PORTAL_LANDING_PAGE_TAG_NAME = 'portal/tracking/landing_page';
const kycTagList = [VERIFIED_TAG_NAME, KYC_1_TAG_NAME, KYC_2_TAG_NAME, UPLOAD_REQUIREMENT_TAG_NAME];

export const useRedirectAfterLogin = () => {
    const navigate = useNavigate();
    const getPlayerTags = useGetPlayerTags();
    const isDesktop = useDeviceSelector(prop('isDesktop'));
    const dynamicTags = useAppSelector(prop('dynamicTags'));
    const navigateWithReplace = useNavigate({ replace: true });
    const { afterLoginUrl } = urlUtils.getURLParams(location.search);
    const {
        DTAG_PROFILE_PAGE = '/',
        DTAG_DEPOSIT_PAGE = '/',
        DTAG_KAMBI_PAGE = '/',
        DTAG_CASINO_PAGE = '/'
    } = dynamicTags;
    const translate = useTranslations('playtech.mobile.system.verificationStatus.popup.');

    return useCallback(({ afterRegisterLogin = false }) => {
        if (!afterRegisterLogin) {
            if (!isDesktop || afterLoginUrl) {
                return;
            }

            getPlayerTags({ tagName: PORTAL_LANDING_PAGE_TAG_NAME })
                .then(({ tags = [] }) => {
                    const pages = {
                        sports: DTAG_KAMBI_PAGE,
                        casino: DTAG_CASINO_PAGE
                    };
                    const currentPage = urlUtils.getCleanUrl();
                    const tagValue = tags.filter(tag => tag.name === PORTAL_LANDING_PAGE_TAG_NAME)[0]?.value;
                    const landingPage = pages[tagValue];

                    if (tagValue
                        && currentPage !== landingPage
                        && Object.values(pages).includes(currentPage)
                    ) {
                        navigateWithReplace(landingPage);
                    }
                })
                .catch(error => log('error', error));

            return;
        }

        getPlayerTags({ tagName: kycTagList.join(',') })
            .then(({ tags = [] }) => {
                const isUserVerified = Boolean(tags.find(tag => tag.name.toLowerCase() === VERIFIED_TAG_NAME));
                const isUserKycMadeOnce = Boolean(tags.find(tag => tag.name.toLowerCase() === KYC_1_TAG_NAME));
                const isUserKycMadeTwice = Boolean(tags.find(tag => tag.name.toLowerCase() === KYC_2_TAG_NAME));
                const uploadRequirements = tags.find(tag => tag.name.toLowerCase() === UPLOAD_REQUIREMENT_TAG_NAME);

                if (isUserVerified) {
                    navigate(DTAG_DEPOSIT_PAGE);

                    window.Playtech.API.popup.showPopup({
                        title: translate('title'),
                        content: translate('text'),
                        btnTitle: translate('declineBtnText')
                    });

                    return;
                }

                if (isUserKycMadeOnce || isUserKycMadeTwice || ['pending', 'optional', 'mandatory'].includes(uploadRequirements.value)) {
                    navigate(DTAG_PROFILE_PAGE);

                    return;
                }

                const afterRegisterRedirectPath = storageService.session.getItem('afterRegisterRedirectPath') || '/';

                navigate(afterRegisterRedirectPath);
            })
            .catch(error => log('error', error));
    }, [
        navigate,
        translate,
        DTAG_PROFILE_PAGE,
        DTAG_DEPOSIT_PAGE,
        DTAG_KAMBI_PAGE,
        DTAG_CASINO_PAGE,
        isDesktop,
        navigateWithReplace,
        afterLoginUrl,
        getPlayerTags
    ]);
};
